/// <reference types="vite/client" />
// Every language's words at run time: one i18next instance with ICU messages.
//
// English is locales/en.json, registered by whoever bundles it (english.ts for the
// staff app and screens, the booking pages a slice of it). Every other language is
// locales/<code>.json, its own chunk fetched from this server when first needed.
// An organisation's words (Settings › Wording) overlay each language's bundle, so
// lookup is organisation(lang) → shipped(lang) → organisation(en) → English.
// A key is never shown: with no English either, the answer is ''.
import i18next from 'i18next'
import ICU from 'i18next-icu'
import { SOURCE_LANGUAGE } from './languages'
import { formatPhrase, type Vars } from './plural'

/** An organisation's words: lang → key → text. */
export type Wording = Record<string, Record<string, string>>

type Table = Record<string, string>

const NS = 'translation'

export const i18n = i18next.createInstance()
void i18n.use(new ICU({ parseErrorHandler: (_err, _key, res, options) => fallbackFormat(res, options as Record<string, unknown>) })).init({
  lng: SOURCE_LANGUAGE,
  fallbackLng: SOURCE_LANGUAGE,
  load: 'currentOnly',
  initAsync: false,
  resources: {},
  keySeparator: false,
  nsSeparator: false,
  returnEmptyString: true,
  interpolation: { escapeValue: false },
  parseMissingKeyHandler: (key: string) => {
    if (import.meta.env?.DEV) console.warn(`[i18n] no English for ${key}`)
    return ''
  },
})

/** A message ICU cannot say (a missing value, a malformed organisation word): our own reader fills what it can. */
function fallbackFormat(res: unknown, options: Record<string, unknown> | undefined): string {
  if (typeof res !== 'string') return ''
  const replace = (options?.replace ?? {}) as Vars
  return formatPhrase(res, String(options?.lng ?? SOURCE_LANGUAGE), replace)
}

let loaders: Record<string, () => Promise<Table>> = {}
try {
  loaders = import.meta.glob<Table>(['./locales/*.json', '!./locales/en.json'], { import: 'default' })
} catch {
  // Outside Vite: English only.
}
const english: Table = {}
const shipped = new Map<string, Table>()
const pending = new Map<string, Promise<void>>()
let wording: Wording = {}
let version = 0
const listeners = new Set<() => void>()

const filled = (table: Table | undefined): Table =>
  Object.fromEntries(Object.entries(table ?? {}).filter(([, text]) => typeof text === 'string' && text.trim() !== ''))

/** Put lang's bundle back together: its file (or English) under the organisation's words. */
function rebuild(lang: string): void {
  const base = lang === SOURCE_LANGUAGE ? english : (shipped.get(lang) ?? {})
  const bundle = { ...base, ...filled(wording[lang]) }
  i18n.removeResourceBundle(lang, NS)
  i18n.addResourceBundle(lang, NS, bundle, false, true)
}

function changed(): void {
  version++
  for (const listener of listeners) listener()
}

/** English words, added to what is there (the staff app adds all, the booking pages theirs). */
export function addEnglish(table: Table): void {
  Object.assign(english, table)
  rebuild(SOURCE_LANGUAGE)
  changed()
}

/** For useSyncExternalStore: re-render when a language arrives or the wording changes. */
export function subscribeWords(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function wordsVersion(): number {
  return version
}

/** The languages this build ships a file for besides English. */
export function shippedLanguages(): string[] {
  return Object.keys(loaders).map((path) => path.replace(/^\.\/locales\/|\.json$/g, ''))
}

export function isLanguageLoaded(code: string): boolean {
  return code === SOURCE_LANGUAGE || shipped.has(code) || !(`./locales/${code}.json` in loaders)
}

/** Fetch a language's file once. A failure leaves English showing; it is asked again next time. */
export function loadLanguage(code: string): Promise<void> {
  if (isLanguageLoaded(code)) return Promise.resolve()
  const running = pending.get(code)
  if (running) return running
  const load = loaders[`./locales/${code}.json`]!()
    .then((table) => registerLanguage(code, table))
    .catch(() => undefined)
    .finally(() => pending.delete(code))
  pending.set(code, load)
  return load
}

/** Put a language's words in place directly (tests, and a screen that already holds them). */
export function registerLanguage(code: string, table: Table): void {
  shipped.set(code, filled(table))
  rebuild(code)
  changed()
}

/** The organisation's words, as identity or a screen's design answer carries them. */
export function setWording(next: Wording | null | undefined): void {
  const clean: Wording = {}
  for (const [lang, words] of Object.entries(next ?? {})) {
    if (words && typeof words === 'object') clean[lang] = { ...words }
  }
  if (JSON.stringify(clean) === JSON.stringify(wording)) return
  const touched = new Set([...Object.keys(wording), ...Object.keys(clean)])
  wording = clean
  for (const lang of touched) rebuild(lang)
  changed()
}

export function currentWording(): Wording {
  return wording
}

/** The shipped word in lang, before any organisation's; undefined when the file has none. */
export function shippedWord(key: string, lang: string): string | undefined {
  if (lang === SOURCE_LANGUAGE) return undefined
  return shipped.get(lang)?.[key]
}

/** The text of key in lang, before plurals and placeholders (a template a caller fills itself). */
export function wordText(key: string, lang: string): string {
  const own = i18n.getResource(lang, NS, key) as string | undefined
  if (own) return own
  return (i18n.getResource(SOURCE_LANGUAGE, NS, key) as string | undefined) ?? ''
}

/** key said in lang: plural forms chosen and placeholders filled (ICU). */
export function sayWord(key: string, lang: string, vars?: Vars): string {
  return i18n.t(key, { lng: lang, replace: withCount(vars) }) as string
}

/** A phrase counting by {count} is also said with only n given ("{n} rows"). */
export function withCount(vars: Vars | undefined): Vars {
  if (!vars) return {}
  return vars.count === undefined && typeof vars.n === 'number' ? { ...vars, count: vars.n } : vars
}
